import { useState, useCallback, useEffect, useRef } from 'react';
import { apiService } from '../services/apiService';
import { filtersToApiParams } from '../config/filterConfig';

const normalizeFeeTotals = (totals) => {
    if (!totals) return null;

    const initialCost = totals.initialCost ?? totals.InitialCost;
    const managementFee = totals.managementFee ?? totals.ManagementFee;
    if (initialCost === undefined && managementFee === undefined) return null;

    const toNumber = (value) => {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : 0;
    };

    return {
        initialCost: toNumber(initialCost),
        managementFee: toNumber(managementFee),
    };
};

export const useTalentSearch = ({ enabled = true } = {}) => {
    const [talents, setTalents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('desc');

    // 筛选状态
    const [filters, setFilters] = useState({});

    // 分页状态
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [feeTotals, setFeeTotals] = useState(null);
    const [pageSize] = useState(50); // 每页50条数据
    const latestRequestRef = useRef(0);

    const fetchTalents = useCallback(async () => {
        const requestId = latestRequestRef.current + 1;
        latestRequestRef.current = requestId;
        setIsLoading(true);
        setFeeTotals(null);
        try {
            // 转换筛选为API参数
            const filterParams = filtersToApiParams(filters);

            const params = {
                searchTerm: searchTerm,
                sortBy: sortBy,
                descending: sortDirection === 'desc',
                page: currentPage,
                pageSize: pageSize,
                noPagination: false, // 启用分页
                ...filterParams, // 合并筛选参数
            };
            const response = await apiService.getTalents(params);

            if (requestId !== latestRequestRef.current) return;

            // 处理分页响应格式
            if (response.data) {
                // 分页模式：response包含data和分页信息
                setTalents(response.data);
                setTotalPages(response.totalPages || 1);
                setTotalCount(response.totalCount || 0);
                setCurrentPage(response.currentPage || 1);
                setFeeTotals(normalizeFeeTotals(response.totals ?? response.Totals));
            } else {
                // 兼容旧格式（无分页）
                setTalents(response);
                setTotalPages(1);
                setTotalCount(response.length || 0);
                setFeeTotals(null);
            }
        } catch (error) {
            if (requestId !== latestRequestRef.current) return;
            console.error("获取人才列表失败:", error);
            alert("获取人才列表失败");
        } finally {
            if (requestId === latestRequestRef.current) {
                setIsLoading(false);
            }
        }
    }, [searchTerm, sortBy, sortDirection, currentPage, pageSize, filters]);

    // 搜索、排序和筛选变化时重置到第一页
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, sortBy, sortDirection, filters]);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        const handler = setTimeout(() => {
            fetchTalents();
        }, 300);

        return () => {
            clearTimeout(handler);
        };
    }, [enabled, fetchTalents]);

    return {
        talents,
        isLoading,
        searchTerm,
        setSearchTerm,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        fetchTalents,
        // 筛选相关
        filters,
        setFilters,
        // 分页相关
        currentPage,
        setCurrentPage,
        totalPages,
        totalCount,
        feeTotals,
        pageSize,
    };
};
